import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import icons from "./data/icons";
import { getBlogs } from "./api/blogApi";
import { getFooter } from "./api/footerService";
import { getContact } from "./api/contactService";
import EmailLink from "./EmailLink";
import { getHome } from "./api/homeService";


// =============================================================
// DESIGN NOTES
// -------------------------------------------------------------
// Visual identity: "field notes" / engineering-log aesthetic.
// Posts are treated as sequential, numbered log entries rather
// than a grid of identical SaaS cards. One accent color (indigo),
// dark ink background, monospace reserved for actual data
// (index, date, counts) — not decoration.
//
// Fonts: Space Grotesk (display), Inter (body), used via bl-display
// / bl-mono utility classes. For production, move the @import
// below into your index.html <head> as <link> tags:
//
//   <link rel="preconnect" href="https://fonts.googleapis.com">
//   <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
// =============================================================

const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');

.bl-root {
  --ink: #0C121D;
  --ink-2: #111827;
  --line: #334155;
  --paper: #1E293B;
  --text: #F8FAFC;
  --muted: #94A3B8;
  --accent: #818CF8;
  --accent-dim: #6366F1;

  background-color: #0C121D;
  font-family: 'Inter', system-ui, sans-serif;

  background-image:
    radial-gradient(rgba(34, 211, 238, 0.06) 1px, transparent 1px);
  background-size: 22px 22px;
}

.bl-display { font-family: 'Space Grotesk', system-ui, sans-serif; }
.bl-mono { font-family: 'Inter', system-ui, sans-serif; }

@keyframes bl-rise {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.bl-enter { animation: bl-rise 0.5s ease-out both; }

.bl-row {
  border-top: 1px solid var(--line);
  transition: background-color 0.2s ease, padding-left 0.2s ease;
}
.bl-row:hover { background-color: var(--ink-2); }
.bl-row:last-child { border-bottom: 1px solid var(--line); }

.bl-search:focus { border-color: var(--accent); }

.bl-placeholder {
  background: linear-gradient(135deg, #172033, #1E293B);
  position: relative;
  overflow: hidden;
}
.bl-placeholder::after {
  content: '';
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    135deg,
    transparent,
    transparent 6px,
    rgba(103, 232, 249, 0.05) 6px,
    rgba(103, 232, 249, 0.05) 7px
  );
}

.bl-tag {
  transition: color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
}

.bl-glow {
  position: absolute;
  top: -120px;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 300px;
  background: radial-gradient(ellipse at center, rgba(34,211,238,0.14), transparent 70%);
  pointer-events: none;
}
`;

// =============================================================
// HEADER
// =============================================================

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [home, setHome] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dateTime, setDateTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setDateTime(new Date());
    }, 100);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadHome = async () => {
      try {
        const res = await getHome();
        const raw = res?.data?.data ?? res?.data ?? null;
        const homeData = Array.isArray(raw) ? raw[0] : raw;
        setHome(homeData || null);
      } catch (error) {
        console.error("Failed to load home data:", error);
      }
    };

    loadHome();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);

    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className="w-full sticky top-0 z-30 transition-all duration-300"
      style={{
        borderBottom: "1px solid var(--line)",
        backgroundColor: scrolled
          ? "rgba(12, 18, 29, 0.85)"
          : "rgba(12, 18, 29, 0.4)",
        backdropFilter: "blur(10px)",
      }}
    >
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between relative">

        {/* LOGO */}
        <a
          href="/"
          className="flex-shrink-0 absolute left-1/2 -translate-x-1/2 lg:static lg:translate-x-0"
        >
          <img
            src={home?.headerLogo}
            alt="Vinayak V Thayil"
            className="h-12 min-[380px]:h-14 sm:h-16 md:h-18 lg:h-20 object-contain transition-all duration-300"
          />
        </a>

        {/* HOME - DESKTOP ONLY */}
        <Link
          to="/"
          className="hidden lg:block absolute left-1/2 -translate-x-1/2 bl-tag bl-mono text-xs px-3 py-1.5 rounded-full uppercase"
          style={{ color: "var(--muted)" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "var(--text)";
            e.currentTarget.style.backgroundColor = "var(--ink-2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "var(--muted)";
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          home
        </Link>

        {/* DESKTOP DATE + TIME + LIVE */}
        <div className="hidden lg:flex items-center gap-5 ml-auto">

          {/* DATE + TIME */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/30 backdrop-blur-sm border border-slate-700/30">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />

            <span className="text-xs text-slate-300 font-medium whitespace-nowrap">
              {dateTime.toLocaleDateString(undefined, {
                weekday: "long",
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
              {" | "}
              {dateTime.toLocaleTimeString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
              })}
            </span>
          </div>

          {/* LIVE */}
          <div
            className="flex items-center gap-2 bl-mono text-[10px] px-3 py-1.5 rounded-full"
            style={{
              border: "1px solid var(--line)",
              color: "var(--muted)",
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ backgroundColor: "var(--accent)" }}
            />
            Live
          </div>

        </div>

        {/* MOBILE + TABLET HAMBURGER */}
        {/* MOBILE + TABLET MENU BUTTON */}
        <div className="flex items-center gap-3 lg:hidden ml-auto">
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className="relative w-10 h-10 rounded-full flex items-center justify-center transition-colors"
            style={{
              color: "var(--muted)",
              background: "rgba(255,255,255,0.06)",
            }}
          >
            {/* TOP LINE */}
            <span
              className="absolute w-5 h-0.5 rounded-full transition-all duration-300 ease-in-out"
              style={{
                background: "currentColor",
                transform: menuOpen
                  ? "rotate(45deg)"
                  : "translateY(-6px)",
              }}
            />

            {/* MIDDLE LINE */}
            <span
              className="absolute w-5 h-0.5 rounded-full transition-all duration-200 ease-in-out"
              style={{
                background: "currentColor",
                opacity: menuOpen ? 0 : 1,
              }}
            />

            {/* BOTTOM LINE */}
            <span
              className="absolute w-5 h-0.5 rounded-full transition-all duration-300 ease-in-out"
              style={{
                background: "currentColor",
                transform: menuOpen
                  ? "rotate(-45deg)"
                  : "translateY(6px)",
              }}
            />
          </button>
        </div>

        {/* MOBILE + TABLET MENU */}
        {/* MOBILE + TABLET MENU */}
        {menuOpen && (
          <div
            className="lg:hidden absolute top-16 left-0 right-0"
            style={{
              backgroundColor: "rgba(12, 18, 29, 0.98)",
              backdropFilter: "blur(12px)",
              borderBottom: "1px solid var(--line)",
            }}
          >
            {/* TOP AREA */}
            <div className="relative h-14 flex items-center justify-center">

              {/* DATE + TIME */}
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/30 border border-slate-700/40">
                <div className="w-2 h-2 bg-green-400 rounded-full" />

                <span className="text-xs text-slate-300 font-medium whitespace-nowrap">
                  {dateTime.toLocaleDateString(undefined, {
                    weekday: "long",
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                  {" | "}
                  {dateTime.toLocaleTimeString(undefined, {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: false,
                  })}
                </span>
              </div>

              {/* LIVE */}

            </div>

            {/* MENU CONTENT */}
            <div
              className="px-7 py-8"
              style={{
                borderTop: "1px solid var(--line)",
              }}
            >
              {/* HOME */}
              <Link
                to="/"
                onClick={() => setMenuOpen(false)}
                className="block bl-mono text-sm uppercase font-semibold"
                style={{ color: "var(--text)" }}
              >
                HOME
              </Link>
            </div>
          </div>
        )}

      </div>
    </header>
  );
}

// =============================================================
// FOOTER
// =============================================================

function Footer() {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const [footer, setFooter] = useState(null);
  const [contactData, setContactData] = useState(null);
  const [home, setHome] = useState(null);
  useEffect(() => {
    const loadHome = async () => {
      try {
        const res = await getHome();
        const raw = res?.data?.data ?? res?.data ?? null;
        const homeData = Array.isArray(raw) ? raw[0] : raw;
        setHome(homeData || null);
      } catch (error) {
        console.error("Failed to load home data:", error);
      }
    };
    loadHome();
  }, []);
  useEffect(() => {
    const loadContact = async () => {
      try {
        const response = await getContact();

        const contact = response?.data?.data;

        setContactData(
          Array.isArray(contact) ? contact[0] : contact
        );
      } catch (error) {
        console.error("Failed to load contact data:", error);
      }
    };

    loadContact();
  }, []);
  useEffect(() => {
    const loadFooter = async () => {
      try {
        const res = await getFooter();
        const raw = res?.data?.data ?? [];
        const footerData = Array.isArray(raw) ? raw[0] : raw;
        setFooter(footerData || null);
      } catch (error) {
        console.error("Failed to load footer:", error);
      }
    };
    loadFooter();
  }, []);

  return (
    <footer
      className="w-full mt-8"
      style={{ borderTop: "1px solid var(--line)" }}
    >
      <div className="max-w-8xl mx-auto px-8 sm:px-10 lg:px-16 py-6">
        <div
          className="flex flex-col sm:flex-row sm:items-start justify-between gap-8 pb-8"
          style={{ borderBottom: "1px solid var(--line)" }}
        >
          <div className="flex flex-col items-start">
            <a href="/" aria-label="Home" className="text-[15px] sm:text-base font-bold tracking-wide">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                {footer?.footername}
              </span>
            </a>

            <p className="mt-1.5 max-w-xs text-slate-400 text-xs leading-snug">
              {footer?.footerdes}
            </p>

            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {footer?.socialLinks
                ?.filter((link) => link.showIcon)
                .map((link) => {
                  const Icon = icons[link.icon];

                  if (!Icon) return null;

                  return (
                    <a
                      key={link.id || link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={link.text || link.title}
                      className="relative group flex items-center justify-center w-9 h-9 rounded-lg
             bg-slate-800/70 border border-transparent
             transition-all duration-300
             hover:-translate-y-1 hover:bg-slate-800"
                      style={{
                        "--icon-color": link.iconColor || "#94a3b8",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = link.iconColor || "#94a3b8";
                        e.currentTarget.style.boxShadow = `0 0 16px ${link.iconColor || "#94a3b8"
                          }`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "transparent";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <Icon
                        className="w-4 h-4 transition-all duration-300
               group-hover:scale-125"
                        style={{
                          color: link.iconColor || "#94a3b8",
                        }}
                      />

                      <span
                        className="
    absolute
    bottom-full
    left-1/2
    -translate-x-1/2
    mb-2

    w-fit
    max-w-[220px]

    px-3
    py-1.5

    rounded-lg
    text-white

    text-xs
    font-semibold
    whitespace-nowrap
    leading-none

    shadow-lg

    opacity-0
    translate-y-1
    group-hover:opacity-100
    group-hover:translate-y-0

    transition-all
    duration-200
    ease-out

    pointer-events-none
    z-50
  "
                        style={{
                          backgroundColor: link.iconColor || "#94a3b8",
                        }}
                      >
                        {link.text || link.title || link.name}

                        <span
                          className="
      absolute
      top-full
      left-1/2
      -translate-x-1/2

      w-0
      h-0

      border-l-[6px]
      border-r-[6px]
      border-t-[6px]

      border-l-transparent
      border-r-transparent
    "
                          style={{
                            borderTopColor: link.iconColor || "#94a3b8",
                          }}
                        />
                      </span>
                    </a>
                  );
                })}

              <EmailLink
                email={contactData?.email}
                icon={
                  <icons.Mail
                    className="w-4 h-4 transition-all duration-300 group-hover:scale-125"
                  />
                }
                className="relative group flex items-center justify-center w-9 h-9 rounded-lg
             bg-slate-800/70 text-slate-400
             border border-transparent
             transition-all duration-300
             hover:-translate-y-1 hover:bg-slate-800 hover:text-cyan-300"
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#67e8f9";
                  e.currentTarget.style.boxShadow = "0 0 16px #67e8f9";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "transparent";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <span
                  className="
    absolute
    bottom-full
    left-1/2
    -translate-x-1/2
    mb-2

    inline-block
    w-fit
    max-w-[220px]

    px-3
    py-1.5

    rounded-lg
    text-white
    text-xs
    font-semibold
    whitespace-nowrap
    leading-none

    shadow-lg

    opacity-0
    translate-y-1
    group-hover:opacity-100
    group-hover:translate-y-0

    transition-all
    duration-200
    ease-out

    pointer-events-none
    z-50
  "
                  style={{
                    backgroundColor: "#37faf4",
                    color: "#000000",
                  }}
                >
                  Email

                  <span
                    className="
      absolute
      top-full
      left-1/2
      -translate-x-1/2
      w-0
      h-0
      border-l-[6px]
      border-r-[6px]
      border-t-[6px]
      border-l-transparent
      border-r-transparent
    "
                    style={{
                      borderTopColor: "#37faf4",
                    }}
                  />
                </span>
              </EmailLink>
            </div>
          </div>
        </div>

        <div className="pt-6 sm:pt-4 mt-6 sm:mt-4 pb-4 md:pb-0 flex flex-col md:flex-row items-center justify-between gap-2 md:gap-1.5 text-xs">                    <p className="text-slate-500 text-center md:text-left leading-snug">
          © {new Date().getFullYear()} {footer?.copyright}
        </p>

          <p className="text-slate-500 text-center flex items-center justify-center leading-snug">
            <span>{footer?.developerDetail?.trim()}</span>
            <a
              href={footer?.footerLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center"
            >
              {home?.footerLogo ? (
                <img
                  src={home.footerLogo}
                  alt="Developer Logo"
                  className="h-8 md:h-10 w-auto object-contain"
                />
              ) : (
                footer?.footername && (
                  <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent font-semibold">
                    {footer.footername}
                  </span>
                )
              )}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

// =============================================================
// BLOG
// =============================================================

function Blog() {
  const [postsData, setPostsData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const POSTS_PER_PAGE = 16;

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await getBlogs();
        const result = response.data;

        if (!result?.success) {
          throw new Error(result?.message || "Failed to fetch blogs");
        }

        setPostsData(result.data || []);
      } catch (err) {
        console.error("Failed to load blogs:", err);
        setError(
          err.response?.data?.message ||
          err.message ||
          "Unable to load blogs. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const getBlogUrl = (post) => {
    if (!post?.encryptedId) return "#";
    return `/blog/${encodeURIComponent(post.encryptedId)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;

    const now = new Date();

    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Future dates
    if (diffMs < 0) {
      const futureMinutes = Math.floor(Math.abs(diffMs) / (1000 * 60));

      if (futureMinutes < 1) return "Just now";
      if (futureMinutes < 60) return `in ${futureMinutes} min`;

      const futureHours = Math.floor(futureMinutes / 60);
      if (futureHours < 24) return `in ${futureHours} hr`;

      const formattedDate = date.toLocaleDateString("en-IN", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

      return `${formattedDate}, ${date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    }

    // Relative time
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "1d ago";
    if (diffDays <= 7) return `${diffDays}d ago`;

    // After 7 days → full date + time
    // After 7 days → full date + time
    const formattedDate = `${date.toLocaleDateString("en-IN", {
      weekday: "short",
    })}, ${date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    })} ${date.getFullYear()}`;

    return `${formattedDate}, ${date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  const categories = useMemo(() => {
    const unique = new Map();

    postsData.forEach((post) => {
      const value = post.category;

      if (value) {
        const key = String(value).trim();

        if (key) {
          unique.set(key.toLowerCase(), key);
        }
      }
    });

    return Array.from(unique.values()).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [postsData]);

  const filteredPosts = useMemo(() => {
    const sortedPosts = [...postsData].sort(
      (a, b) =>
        new Date(b.date || 0).getTime() -
        new Date(a.date || 0).getTime()
    );

    const query = searchQuery.trim().toLowerCase();

    return sortedPosts.filter((post) => {
      // Category filter uses ONLY the database category field.
      const categoryValue = String(post.category || "")
        .trim()
        .toLowerCase();

      const matchesCategory =
        selectedCategory === "all" ||
        categoryValue === selectedCategory.toLowerCase();

      const matchesSearch =
        !query ||
        post.title?.toLowerCase().includes(query) ||
        post.excerpt?.toLowerCase().includes(query) ||
        post.category?.toLowerCase().includes(query) ||
        post.categoryLabel?.toLowerCase().includes(query) ||
        post.authorName?.toLowerCase().includes(query) ||
        post.authorTitle?.toLowerCase().includes(query);

      return matchesCategory && matchesSearch;
    });
  }, [postsData, searchQuery, selectedCategory]);

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);

  const currentPosts = useMemo(() => {
    const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);
  }, [filteredPosts, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const changePage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const paginationItems = useMemo(() => {
    const items = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) items.push(i);
      return items;
    }

    items.push(1);
    if (currentPage > 4) items.push("left-dots");

    const startPage = Math.max(2, currentPage - 1);
    const endPage = Math.min(totalPages - 1, currentPage + 1);

    for (let i = startPage; i <= endPage; i++) items.push(i);

    if (currentPage < totalPages - 3) {
      items.push("right-dots");
    }

    items.push(totalPages);
    return items;
  }, [currentPage, totalPages]);

  if (loading) {
    return (
      <section className="bl-root min-h-screen flex items-center justify-center">
        <style>{FONT_IMPORT}</style>
        <div className="min-h-screen w-full flex items-center justify-center bg-[#061321]">
          <div className="h-14 w-14 animate-spin rounded-full border-[5px] border-slate-700 border-t-[#6366F1]" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="bl-root min-h-screen flex items-center justify-center px-6">
        <style>{FONT_IMPORT}</style>
        <div className="text-center max-w-sm">
          <p
            className="bl-mono text-xs mb-3 uppercase tracking-[0.25em]"
            style={{ color: "var(--accent)" }}
          >
            error
          </p>
          <p className="mb-6" style={{ color: "var(--text)" }}>
            {error}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="bl-mono text-sm px-5 py-2.5 border rounded-full transition-all hover:bg-cyan-400/10"
            style={{
              borderColor: "var(--accent)",
              color: "var(--accent)",
            }}
          >
            try again
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className="bl-root min-h-screen relative overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 50% 0%, rgba(8,145,178,0.10), transparent 34%), #061321",
      }}
    >
      <style>{FONT_IMPORT}</style>

      <div className="bl-glow" />

      <Header />

      <main className="relative z-10">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 lg:pt-10 pb-10 bl-enter">

          {/* HERO */}
          <section className="relative mb-7 sm:mb-9">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 lg:gap-9 items-end">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span
                    className="bl-mono text-[10px] sm:text-[11px] tracking-[0.35em] uppercase"
                    style={{ color: "var(--accent)" }}
                  >
                    {filteredPosts.length} published articles
                  </span>
                </div>

                <h1
                  className="bl-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-[0.95]"
                  style={{ color: "var(--text)" }}
                >
                  Field{" "}
                  <span
                    style={{
                      color: "var(--accent)",
                      textShadow: "0 0 30px rgba(103,232,249,0.18)",
                    }}
                  >
                    Notes
                  </span>
                </h1>

                <p
                  className="mt-3 max-w-xl text-xs sm:text-sm lg:text-base leading-6"
                  style={{ color: "var(--muted)" }}
                >
                  Notes on things I build, break, and learn along the way.
                </p>
              </div>

              {/* SEARCH + CATEGORY */}
              <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 w-full lg:w-auto">
                <div className="relative w-full sm:w-[260px] lg:w-[260px]">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search blogs..."
                    className="bl-search w-full h-10 rounded-full bg-white/[0.02] border px-4 pr-10 text-xs sm:text-sm outline-none transition-all duration-300 focus:bg-cyan-400/[0.03]"
                    style={{
                      borderColor: "rgba(103,232,249,0.28)",
                      color: "var(--text)",
                    }}
                  />

                  <icons.Search
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4"
                    style={{ color: "var(--muted)" }}
                  />
                </div>
                <div className="relative w-full sm:w-[180px] lg:w-[240px] xl:w-[190px]">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full h-10 appearance-none rounded-xl sm:rounded-full bg-white/[0.02] border px-3 pr-9 text-[10px] sm:text-xs outline-none cursor-pointer transition-all duration-300 focus:bg-cyan-400/[0.03]"
                    style={{
                      borderColor: "rgba(103,232,249,0.28)",
                      color: "var(--text)",
                    }}
                  >
                    <option
                      value="all"
                      style={{
                        backgroundColor: "#071525",
                        color: "#F8FAFC",
                      }}
                    >
                      ALL CATEGORIES ({postsData.length})
                    </option>

                    {categories.map((category) => {
                      const count = postsData.filter(
                        (post) =>
                          String(post.category || "").trim().toLowerCase() ===
                          String(category).trim().toLowerCase()
                      ).length;

                      return (
                        <option
                          key={category}
                          value={category}
                          style={{
                            backgroundColor: "#071525",
                            color: "#F8FAFC",
                          }}
                        >
                          {category.toUpperCase()} ({count})
                        </option>
                      );
                    })}
                  </select>

                  <span
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs"
                    style={{ color: "var(--muted)" }}
                  >
                    <icons.ChevronDown size={14} />
                  </span>
                </div>
              </div>
            </div>

            <div
              className="mt-6 h-px w-full"
              style={{
                background:
                  "linear-gradient(90deg, rgba(103,232,249,0.38), rgba(51,65,85,0.55), transparent)",
              }}
            />
          </section>

          {/* BLOG GRID — FULL WIDTH, NO RECENT SIDEBAR */}
          {currentPosts.length === 0 ? (
            <div
              className="text-center py-24 rounded-2xl border"
              style={{
                color: "var(--muted)",
                borderColor: "rgba(103,232,249,0.12)",
                backgroundColor: "rgba(15,23,42,0.45)",
              }}
            >
              <p
                className="bl-mono text-xs mb-2 uppercase tracking-[0.25em]"
                style={{ color: "var(--accent)" }}
              >
                no results
              </p>
              <p className="text-sm">
                Nothing matches your search or selected category.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1.5 sm:gap-2 w-full">
              {currentPosts.map((post, index) => (
                <Link
                  key={post.id}
                  to={getBlogUrl(post)}
                  className="group block h-full"
                >
                  <article
                    className="h-full overflow-hidden rounded-2xl border transition-all duration-300 hover:-translate-y-1"
                    style={{
                      borderColor: "rgba(56,189,248,0.26)",
                      background:
                        "linear-gradient(180deg, rgba(9,30,48,0.96), rgba(5,20,34,0.98))",
                      boxShadow: "0 12px 35px rgba(0,0,0,0.18)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor =
                        "rgba(34,211,238,0.65)";
                      e.currentTarget.style.boxShadow =
                        "0 18px 45px rgba(0,0,0,0.28), 0 0 28px rgba(34,211,238,0.06)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor =
                        "rgba(56,189,248,0.26)";
                      e.currentTarget.style.boxShadow =
                        "0 12px 35px rgba(0,0,0,0.18)";
                    }}
                  >
                    {/* COVER IMAGE */}
                    <div className="relative h-[140px] sm:h-[150px] lg:h-[145px] xl:h-[155px] overflow-hidden">
                      {post.coverImage ? (
                        <img
                          src={post.coverImage}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                        />
                      ) : (
                        <div className="bl-placeholder w-full h-full" />
                      )}

                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            "linear-gradient(180deg, transparent 45%, rgba(4,15,27,0.72) 100%)",
                        }}
                      />

                      <div
                        className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center border backdrop-blur-md"
                        style={{
                          borderColor: "rgba(103,232,249,0.38)",
                          backgroundColor: "rgba(3,14,25,0.55)",
                          color: "var(--accent)",
                        }}
                      >
                        <span className="bl-mono text-[10px]">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                      </div>
                    </div>

                    {/* CONTENT */}
                    <div className="p-3 sm:p-3.5 flex flex-col min-h-[145px]">
                      {(post.category || post.categoryLabel) && (
                        <div className="mb-3 flex items-center gap-2">
                          {post.category && (
                            <span
                              className="bl-mono text-[10px] font-semibold hidden tracking-[0.18em] uppercase"
                              style={{ color: "var(--accent)" }}
                            >
                              {post.category}
                            </span>
                          )}

                          {post.categoryLabel && (
                            <span
                              className="bl-mono text-[10px] font-semibold tracking-[0.18em] uppercase"
                              style={{ color: "var(--muted)" }}
                            >
                              {post.categoryLabel}
                            </span>
                          )}
                        </div>
                      )}

                      <h2
                        className="bl-display text-[15px] sm:text-base font-bold leading-[1.18] line-clamp-3 transition-colors duration-300 group-hover:text-cyan-300"
                        style={{ color: "var(--text)" }}
                      >
                        {post.title}
                      </h2>

                      {post.excerpt && (
                        <p
                          className="mt-1.5 text-xs leading-5 line-clamp-2"
                          style={{ color: "var(--muted)" }}
                        >
                          {post.excerpt}
                        </p>
                      )}

                      <div
                        className="mt-auto pt-3 flex items-center justify-between gap-2 border-t"
                        style={{ borderColor: "rgba(51,65,85,0.72)" }}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="font-mono text-xs whitespace-nowrap"
                            style={{ color: "var(--muted)" }}
                          >
                            {formatDate(post.date)}
                          </span>
                        </div>

                        <span
                          className="flex-shrink-0 w-7 h-7 rounded-full border flex items-center justify-center text-sm transition-all duration-300 group-hover:translate-x-0.5 group-hover:bg-cyan-400/10"
                          style={{
                            borderColor: "var(--accent)",
                            color: "var(--accent)",
                          }}
                        >
                          →
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div
              className="flex flex-wrap items-center justify-center gap-1 mt-9 pt-6"
              style={{ borderTop: "1px solid var(--line)" }}
            >
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => changePage(currentPage - 1)}
                className="bl-mono text-xs px-3 py-2 rounded-full transition-all disabled:opacity-30 hover:bg-cyan-400/10"
                style={{ color: "var(--text)" }}
              >
                ← prev
              </button>

              {paginationItems.map((item, index) => {
                if (item === "left-dots" || item === "right-dots") {
                  return (
                    <span
                      key={`${item}-${index}`}
                      className="bl-mono text-xs px-1"
                      style={{ color: "var(--muted)" }}
                    >
                      …
                    </span>
                  );
                }

                const isActive = currentPage === item;

                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => changePage(item)}
                    className="bl-mono text-xs w-8 h-8 rounded-full transition-all"
                    style={{
                      color: isActive ? "#061321" : "var(--text)",
                      backgroundColor: isActive
                        ? "var(--accent)"
                        : "transparent",
                      border: isActive
                        ? "1px solid var(--accent)"
                        : "1px solid transparent",
                    }}
                  >
                    {String(item).padStart(2, "0")}
                  </button>
                );
              })}

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => changePage(currentPage + 1)}
                className="bl-mono text-xs px-3 py-2 rounded-full transition-all disabled:opacity-30 hover:bg-cyan-400/10"
                style={{ color: "var(--text)" }}
              >
                next →
              </button>
            </div>
          )}

          {filteredPosts.length > 0 && (
            <p
              className="bl-mono text-center text-[11px] mt-4"
              style={{ color: "var(--muted)" }}
            >
              {(currentPage - 1) * POSTS_PER_PAGE + 1}–
              {Math.min(
                currentPage * POSTS_PER_PAGE,
                filteredPosts.length
              )}{" "}
              of {filteredPosts.length}
            </p>
          )}
        </div>
      </main>

      <Footer />
    </section>
  );
}

export default Blog;